import {
  TagGroupResponse,
  TagResponse,
} from "@redotech/effect-api-shared/rpc/tag";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { Schema } from "effect";
import {
  supportTicketQueryScopeKey,
  type SupportTicketQueryScope,
} from "./support-ticket-query-keys";

const FIVE_MINUTES_MS = 5 * 60 * 1000;

const SUPPORT_TICKET_TAG_QUERY_KEY_ROOT = ["supportTicketTags"] as const;

export type SupportTicketTagQueryScope = SupportTicketQueryScope;

type SupportTicketTagScopeKey =
  | readonly ["account", string]
  | readonly ["team", string]
  | readonly ["unscoped"];

export function supportTicketTagScopeKey(
  scope?: SupportTicketTagQueryScope,
): SupportTicketTagScopeKey {
  return supportTicketQueryScopeKey(scope);
}

export const supportTicketTagQueryKeys = {
  all: SUPPORT_TICKET_TAG_QUERY_KEY_ROOT,
  accountTags: (scope?: SupportTicketTagQueryScope) =>
    [
      ...SUPPORT_TICKET_TAG_QUERY_KEY_ROOT,
      "accountTags",
      ...supportTicketTagScopeKey(scope),
    ] as const,
};

export interface AccountTagsResponse {
  tags: readonly TagResponse[];
  tagGroups: readonly TagGroupResponse[];
}

export interface SupportTicketTagsApi {
  getAccountTags(): Promise<AccountTagsResponse>;
}

const queryAccountTags = (api: SupportTicketTagsApi) => async () =>
  api.getAccountTags();

export function createTagsCollection({
  queryClient,
  api,
  scope,
}: {
  queryClient: QueryClient;
  api: SupportTicketTagsApi;
  scope?: SupportTicketTagQueryScope;
}) {
  return createCollection(
    queryCollectionOptions({
      queryKey: supportTicketTagQueryKeys.accountTags(scope),
      queryFn: queryAccountTags(api),
      select: (data) => [...data.tags],
      getKey: (item) => item.id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
      schema: Schema.standardSchemaV1(TagResponse),
    }),
  );
}

export function createTagGroupsCollection({
  queryClient,
  api,
  scope,
}: {
  queryClient: QueryClient;
  api: SupportTicketTagsApi;
  scope?: SupportTicketTagQueryScope;
}) {
  return createCollection(
    queryCollectionOptions({
      queryKey: supportTicketTagQueryKeys.accountTags(scope),
      queryFn: queryAccountTags(api),
      select: (data) => [...data.tagGroups],
      getKey: (item) => item.id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
      schema: Schema.standardSchemaV1(TagGroupResponse),
    }),
  );
}

export type TagsCollection = ReturnType<typeof createTagsCollection>;
export type TagGroupsCollection = ReturnType<typeof createTagGroupsCollection>;
